import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Trophy, MessageCircle, Users, Activity, Plus, X, TrendingUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import { absoluteUrl } from '../utils/seo';
import { buildOgImageUrl } from '../utils/ogImage';
import { calculateForumScore } from '../utils/forumScore';
import { getForumGrowthPercent } from '../utils/forumGrowth';
import { formatStatDelta } from '../utils/forumStatChange';
import type { ForumBase } from '../types/forum';

interface Props {
  darkMode: boolean;
}

const CompareForums: React.FC<Props> = ({ darkMode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [forums, setForums] = useState<ForumBase[]>([]);
  const [selectedForums, setSelectedForums] = useState<ForumBase[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const pageSeo = useMemo(() => {
    if (selectedForums.length >= 2) {
      const names = selectedForums.map((f) => f.name).join(' vs ');
      const title = `${names} — Forum Karşılaştırma`;
      return {
        title,
        description: `${names} forumlarının üye, konu, ileti ve skor istatistikleri yan yana.`,
        keywords: 'forum karşılaştırma, forum analizi, forum istatistikleri',
        canonical: absoluteUrl('/compare'),
        ogImage: buildOgImageUrl({
          title: 'Forum Karşılaştırma',
          subtitle: names,
          line1: selectedForums[0]
            ? `${selectedForums[0].members_count.toLocaleString('tr-TR')} üye`
            : undefined,
          line2: selectedForums[1]
            ? `${selectedForums[1].members_count.toLocaleString('tr-TR')} üye`
            : undefined,
          badge: 'Karşılaştırma',
        }),
      };
    }
    return {
      title: 'Forum Karşılaştırma - İstatistik Analizi',
      description:
        'Forum siteleri karşılaştırma aracı. Üye sayıları, konu sayıları ve ileti sayılarına göre detaylı forum karşılaştırması yapın.',
      keywords: 'forum karşılaştırma, forum analizi, forum istatistikleri, forum kıyaslama',
      canonical: absoluteUrl('/compare'),
    };
  }, [selectedForums]);

  usePageSeo(pageSeo, [selectedForums]);

  useEffect(() => {
    fetchForums();
  }, []);

  const fetchForums = async () => {
    try {
      const { data, error } = await supabase
        .from('forums')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      setForums(data || []);
    } catch (error) {
      console.error('Error fetching forums:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (forums.length === 0) return;
    const ids = [searchParams.get('a'), searchParams.get('b'), searchParams.get('c')].filter(
      Boolean
    ) as string[];
    if (ids.length === 0) return;
    const picked = ids
      .map((slug) => forums.find((f) => f.forum_id === slug))
      .filter((f): f is ForumBase => !!f)
      .slice(0, 3);
    if (picked.length) setSelectedForums(picked);
  }, [forums, searchParams]);

  const syncUrl = (list: ForumBase[]) => {
    const p = new URLSearchParams();
    list.forEach((f, i) => p.set(['a', 'b', 'c'][i], f.forum_id));
    setSearchParams(p, { replace: true });
  };

  const handleSelectForum = (forum: ForumBase) => {
    if (selectedForums.length < 3 && !selectedForums.find((f) => f.id === forum.id)) {
      const next = [...selectedForums, forum];
      setSelectedForums(next);
      syncUrl(next);
    }
  };

  const handleRemoveForum = (forumId: string) => {
    const next = selectedForums.filter((f) => f.id !== forumId);
    setSelectedForums(next);
    syncUrl(next);
  };

  const filteredForums = forums.filter(
    (forum) =>
      forum.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !selectedForums.find((f) => f.id === forum.id)
  );

  const metrics = useMemo(
    () =>
      [
        { key: 'score', label: 'Forum skoru', fn: (f: ForumBase) => calculateForumScore(f.topics_count, f.posts_count, f.members_count, f.founded_year) },
        { key: 'topics', label: 'Konu', fn: (f: ForumBase) => f.topics_count },
        { key: 'posts', label: 'İleti', fn: (f: ForumBase) => f.posts_count },
        { key: 'members', label: 'Üye', fn: (f: ForumBase) => f.members_count },
      ] as const,
    []
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-4 ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        >
          <ArrowLeft size={20} /> Geri Dön
        </Link>
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Forum Karşılaştırma</h1>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
            En fazla 3 forum seçin. URL paylaşılabilir: ?a=slug1&b=slug2
          </p>
        </div>

        {selectedForums.length >= 2 && (
          <div className={`mb-8 overflow-x-auto rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'}`}>
            <table className="w-full text-sm">
              <thead>
                <tr className={darkMode ? 'border-gray-700' : 'border-gray-200'}>
                  <th className="text-left p-4">Metrik</th>
                  {selectedForums.map((f) => (
                    <th key={f.id} className="p-4 text-center">
                      <Link to={`/forum/${f.forum_id}`} className="text-indigo-500 hover:underline">
                        {f.name}
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.map((m) => (
                  <tr key={m.key} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <td className="p-4 font-medium">{m.label}</td>
                    {selectedForums.map((f) => (
                      <td key={f.id} className="p-4 text-center font-semibold">
                        {m.fn(f).toLocaleString('tr-TR')}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                  <td className="p-4 font-medium flex items-center gap-1">
                    <TrendingUp size={14} /> Üye büyümesi
                  </td>
                  {selectedForums.map((f) => {
                    const g = getForumGrowthPercent(f, 'members');
                    return (
                      <td key={f.id} className={`p-4 text-center ${g && g > 0 ? 'text-green-500' : ''}`}>
                        {g !== null ? `${g > 0 ? '+' : ''}${g.toFixed(1)}%` : '—'}
                        {formatStatDelta(f.members_count, f.previous_month_members_count) && (
                          <span className="block text-xs opacity-80">
                            {formatStatDelta(f.members_count, f.previous_month_members_count)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="relative mb-4">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} size={20} />
              <input
                type="text"
                placeholder="Forum ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'border-gray-200'} focus:ring-2 focus:ring-indigo-500`}
              />
            </div>
            <div className="grid gap-4 max-h-[500px] overflow-y-auto">
              {filteredForums.map((forum) => (
                <button
                  key={forum.id}
                  type="button"
                  onClick={() => handleSelectForum(forum)}
                  className={`w-full text-left ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'} rounded-xl p-4 shadow-sm`}
                >
                  <div className="flex items-center gap-4">
                    {forum.logo_url && (
                      <img src={forum.logo_url} alt="" className="w-12 h-12 object-contain rounded-lg" />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold">{forum.name}</h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{forum.category}</p>
                    </div>
                    <Plus size={20} className="text-indigo-500" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-4 sm:p-6 shadow-lg lg:sticky lg:top-8`}>
              <h2 className="text-xl font-semibold mb-4">Seçilen Forumlar ({selectedForums.length}/3)</h2>
              {selectedForums.length === 0 ? (
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Karşılaştırmak için forum seçin</p>
              ) : (
                <div className="space-y-4">
                  {selectedForums.map((forum) => (
                    <div key={forum.id} className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4 relative`}>
                      <button
                        type="button"
                        onClick={() => handleRemoveForum(forum.id)}
                        className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                        aria-label="Kaldır"
                      >
                        <X size={16} />
                      </button>
                      <h3 className="font-medium pr-6">{forum.name}</h3>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                        <span className="flex items-center gap-1">
                          <Trophy className="text-amber-500" size={14} />
                          {calculateForumScore(forum.topics_count, forum.posts_count, forum.members_count, forum.founded_year).toLocaleString('tr-TR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="text-blue-500" size={14} />
                          {forum.members_count.toLocaleString('tr-TR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="text-purple-500" size={14} />
                          {forum.topics_count.toLocaleString('tr-TR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Activity className="text-green-500" size={14} />
                          {forum.posts_count.toLocaleString('tr-TR')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareForums;
